import { type MutableRefObject, type ReactNode, type Ref, type RefObject, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { usePopStateDetector } from '@/hooks/usePopStateDetector';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { ProductContentFrameContext } from '@/shared/hooks/useIsInProductContentFrame';
import { setProductContentFrameScrollContainer } from '@/shared/hooks/useProductContentFrameScrollContainer';
import { useScrollRestoration } from '@/shared/hooks/useScrollRestoration';
import { cn } from '@/utils/tailwind.util';
const FRAME_OUTER_CLASS_NAME = 'min-[769px]:pl-md min-[769px]:pr-xs min-[769px]:pt-sm';
const FRAME_CLASS_NAME = 'relative bg-default min-[769px]:flex min-[769px]:h-[calc(100dvh-70px)] min-[769px]:[body:has([datatype=trial-end-discount-banner])_&]:h-[calc(100dvh-110px)] min-[769px]:flex-col min-[769px]:overflow-hidden min-[769px]:rounded-t-md min-[769px]:border min-[769px]:border-alternative min-[769px]:shadow-sm';
const FRAME_SCROLL_CLASS_NAME = 'relative min-w-0 min-[769px]:min-h-0 min-[769px]:flex-1 min-[769px]:overflow-y-auto min-[769px]:rounded-t-md min-[769px]:[scrollbar-gutter:stable]';

/**
 * 좌측 메뉴 화면을 떠나 들어온 화면(걸어온 길의 자식 칸)을 좌측 메뉴 옆 카드에 담고, 왼쪽 위에 목록 버튼(parentLink) 자리를 둔다.
 * 내 폴더 전체는 MyQuizPageFrame 안에 둔다(디자인 결정).
 *
 * 좌측 메뉴가 없는 외부 플랫폼에서는 카드·목록 버튼 없이 자식만 그린다 — 이 규칙은 여기서만 정하고,
 * 하위 레이아웃은 `useIsInProductContentFrame` 로 읽는다.
 * - `parentLink` 는 부모 칸으로 돌아가는 목록 버튼이다.
 * - `footer` 는 카드 맨 아래(스크롤 영역 밖)에 둔다.
 * - `scrollRef` 로 스크롤 영역을 받고, 전역에도 등록해 「맨 위로」 버튼이 카드 스크롤을 따라가게 한다.
 * - `ready` 는 목록이 그려졌는지다. 뒤로·앞으로 가기로 돌아오면 그때 카드 스크롤을 떠날 때 위치로 되돌린다.
 */
export const ProductContentFrame = ({
  ready,
  parentLink,
  hasBanner = true,
  footer,
  scrollRef,
  children
}: {
  ready: boolean;
  parentLink?: ReactNode;
  hasBanner?: boolean;
  footer?: ReactNode;
  scrollRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}) => {
  const enabled = !useIsInExternalPlatform();
  const frameScrollRef = useRef<HTMLDivElement | null>(null);
  const registerScrollContainer = useScrollContainerRef(enabled, scrollRef, frameScrollRef);
  const {
    asPath
  } = useRouter();
  const link = enabled ? parentLink : undefined;
  return <ProductContentFrameContext.Provider value={enabled} data-sentry-element="unknown" data-sentry-component="ProductContentFrame" data-sentry-source-file="ProductContentFrame.tsx">
      <div className={cn(enabled && FRAME_OUTER_CLASS_NAME)}>
        {/* 같은 화면 안 이동(퀴즈 → 다른 퀴즈)에도 뒤로 가기 여부를 새로 읽도록 주소마다 다시 마운트한다. */}
        <FrameScrollRestoration key={asPath} target={frameScrollRef} ready={ready} enabled={enabled} data-sentry-element="FrameScrollRestoration" data-sentry-source-file="ProductContentFrame.tsx" />
        <div className={cn(enabled && FRAME_CLASS_NAME)}>
          <div ref={registerScrollContainer} className={cn(enabled && FRAME_SCROLL_CLASS_NAME)}>
            {!!link && !hasBanner && <div className="px-md pb-lg pt-md">{link}</div>}
            {!!link && hasBanner && <>
                <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[92px] bg-gradient-to-b from-[rgba(28,27,31,0.24)] to-transparent" />
                <div className="absolute left-md top-md z-10">{link}</div>
              </>}
            {children}
          </div>
          {footer}
        </div>
      </div>
    </ProductContentFrameContext.Provider>;
};

/**
 * 카드 스크롤 위치를 주소별로 저장하고, 뒤로·앞으로 가기로 돌아왔을 때만 목록이 그려진 뒤 되돌린다.
 * 새로 들어오면 카드를 맨 위에서 시작한다. Next 의 복원은 창 스크롤만 다뤄 카드 안까지 닿지 않는다.
 * 모바일은 카드 대신 창이 스크롤해 카드 영역 값이 늘 0 이라 아무것도 바뀌지 않는다.
 */
const FrameScrollRestoration = ({
  target,
  ready,
  enabled
}: {
  target: RefObject<HTMLDivElement | null>;
  ready: boolean;
  enabled: boolean;
}) => {
  const isPopState = usePopStateDetector();
  useScrollRestoration(ready, isPopState, {
    target,
    enabled
  });
  return null;
};

/** 스크롤 영역을 부모의 `scrollRef`·카드 안 ref 에 넘기고, 카드가 켜져 있으면 전역 스크롤 영역으로도 등록한다. */
const useScrollContainerRef = (enabled: boolean, scrollRef: Ref<HTMLDivElement> | undefined, frameScrollRef: MutableRefObject<HTMLDivElement | null>) => {
  const registeredRef = useRef<HTMLDivElement | null>(null);
  return useCallback((node: HTMLDivElement | null) => {
    frameScrollRef.current = node;
    if (typeof scrollRef === 'function') scrollRef(node);
    // Ref 타입은 current 를 읽기 전용으로 두지만, 부모가 넘기는 건 useRef 객체라 직접 채운다.
    else if (scrollRef) (scrollRef as MutableRefObject<HTMLDivElement | null>).current = node;
    const next = enabled ? node : null;
    setProductContentFrameScrollContainer(next, registeredRef.current);
    registeredRef.current = next;
  }, [enabled, scrollRef, frameScrollRef]);
};

/** 배너 없는 화면(프로필·공개 폴더 전체·인기 퀴즈)의 ListLayoutWrapper 에 덧붙이는 클래스. */
export const PLAIN_FRAME_BODY_CLASS_NAME = 'pt-none max-tablet-lg:pt-none max-mobile-lg:pt-none min-[769px]:px-2xl';